import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin } from "lucide-react";
import Reveal from "../Reveal";
import { IMAGES } from "../../lib/brand";

const STATS = [
  { k: "01", v: "Charged with Positive Affirmations" },
  { k: "02", v: "Enhanced with Solfeggio Sound Therapy" },
  { k: "03", v: "Enriched with Water Prayers" },
];

export const Origin = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="origin" data-testid="origin-section" className="bg-[#030303]">
      {/* Provenance banner */}
      <div ref={ref} className="relative h-[90vh] overflow-hidden">
        <motion.img
          style={{ y }}
          src={IMAGES.mint}
          alt="Manifest Waterboat among fresh mint from Nashik"
          className="absolute inset-0 h-[116%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-black/50 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#030303]/70 to-transparent" />

        <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-12 h-full flex flex-col justify-end pb-20">
          <Reveal>
            <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.4em] text-teal-300">
              <MapPin size={14} /> THE ORIGIN · #NASHIKCHAPANI
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif font-light tracking-tighter text-5xl md:text-8xl text-zinc-50 leading-[0.95] max-w-4xl">
              Proudly extracted &amp; packed in{" "}
              <span className="italic text-teal-300">Nashik.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-lg text-zinc-300 font-light text-lg">
              Drawn from the sacred aquifers of Nashik and bottled with
              reverence — the water that carries your intention into the world.
            </p>
          </Reveal>
        </div>
      </div>

      {/* Stats */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-3 gap-10 border-b border-white/5">
        {STATS.map((s, i) => (
          <Reveal key={s.k} delay={i * 0.1}>
            <div data-testid={`stat-${i}`} className="border-l border-teal-400/30 pl-6">
              <p className="font-serif text-4xl md:text-5xl text-teal-300/80">{s.k}</p>
              <p className="mt-3 font-light text-lg md:text-xl text-zinc-200 leading-snug max-w-[16rem]">
                {s.v}
              </p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Product bento */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-24">
        <Reveal className="mb-12 md:mb-16">
          <h3 className="font-serif font-light tracking-tight text-4xl md:text-6xl text-zinc-100">
            Our tiny <span className="italic text-teal-300">magic bombs!</span>
          </h3>
        </Reveal>
        <Reveal y={50}>
          <div className="group relative mx-auto w-full max-w-2xl overflow-hidden rounded-sm border border-white/10 aspect-[7/8]">
            <img
              src="/family/wb-duo-forest.png"
              alt="Waterboat 250ml and 200ml bottles on a mossy rock in a misty forest"
              className="h-full w-full object-cover object-center transition-transform duration-[1200ms] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8">
              <p className="font-serif text-3xl md:text-4xl text-zinc-50">
                Waterboat 250ml &amp; 200ml
              </p>
              <p className="mt-2 font-mono text-[11px] tracking-[0.12em] text-teal-300">
                Small thirst. Small pick. Big refresh.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Origin;
